import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { firebaseService } from '../../services/firebase';
import { leadGenService } from '../../services/leadGeneration';
import { storageService } from '../../utils/storage';
import toast from 'react-hot-toast';

const UserProfile = () => {
  const navigate = useNavigate();
  const { 
    user, 
    backendUser, 
    updateProfile, 
    signOut, 
    isAnonymous, 
    isEmailVerified, 
    getUserType, 
    getRemainingRequests, 
    getRequestLimit,
    resendEmailVerification,
    refreshUserStatus,
    fetchUserData
  } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [formData, setFormData] = useState({
    display_name: backendUser?.display_name || user?.displayName || '',
    first_name: backendUser?.first_name || '',
    last_name: backendUser?.last_name || '',
  });

  const [unsavedPlaces, setUnsavedPlaces] = useState([]);
  const [isSavingPlaces, setIsSavingPlaces] = useState(false);
  const [showUnsavedPlaces, setShowUnsavedPlaces] = useState(false);

  useEffect(() => {
    const data = storageService.getUnsavedSearchResults();
    if (data && data.results && data.results.length > 0) {
      setUnsavedPlaces(data.results); 
    }
  }, []);

  useEffect(() => {
    let interval;
    if (user && !user.isAnonymous && !isEmailVerified()) {
      setCheckingVerification(true);
      interval = setInterval(async () => {
        try {
          await firebaseService.auth.currentUser?.reload();
          const currentUser = firebaseService.getCurrentUser();
          if (currentUser && currentUser.emailVerified) {
            setCheckingVerification(false);
            await refreshUserStatus();
            await fetchUserData();
            toast.success("Email verified successfully. Sign back in to see your upgrades!");
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        } catch (err) {
          console.error(err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [user, isEmailVerified, refreshUserStatus, fetchUserData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await updateProfile(formData);
    setLoading(false);

    if (result.success) {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      display_name: backendUser?.display_name || user?.displayName || '',
      first_name: backendUser?.first_name || '',
      last_name: backendUser?.last_name || '',
    });
    setIsEditing(false);
  };

  const handleResendVerification = async () => {
    setLoading(true);
    await resendEmailVerification();
    setLoading(false);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const handleSaveUnsavedPlaces = async () => {
    if (unsavedPlaces.length === 0) {
      toast.error('No places to save');
      return;
    }

    setIsSavingPlaces(true);

    const { data, error } = await leadGenService.savePlaces(unsavedPlaces, null);

    if (data) {
      toast.success(`Saved ${unsavedPlaces.length} places! ${data.total_existing > 0 ? `(${data.total_existing} already existed)` : ''}`);
      storageService.removeUnsavedSearchResults();
      setUnsavedPlaces([]);
      setShowUnsavedPlaces(false);
    } else if (error) {
      toast.error('Failed to save places');
      console.error('Error saving places:', error);
    }

    setIsSavingPlaces(false);
  };

  const handleDiscardUnsavedPlaces = () => {
    if (window.confirm('Are you sure you want to discard these unsaved places? This action cannot be undone.')) {
      storageService.removeUnsavedSearchResults();
      setUnsavedPlaces([]);
      setShowUnsavedPlaces(false);
      toast.success('Unsaved places discarded');
    }
  };

  const getUserTypeDisplay = () => {
    const type = getUserType();
    const typeMap = {
      'guest': { label: 'Guest', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'standard': { label: 'Standard', color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'admin': { label: 'Admin', color: 'bg-purple-100 text-purple-800 border-purple-200' }
    };
    return typeMap[type] || typeMap['guest'];
  };

  const usagePercentage = ((getRequestLimit() - getRemainingRequests()) / getRequestLimit()) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-screen mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Go to Dashboard"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Profile</h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/documentation')}
                className="px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 text-sm flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Documentation</span>
              </button>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Alert Banners */}
        {(isAnonymous() || (user && !user.isAnonymous && !isEmailVerified())) && (
          <div className="space-y-3 mb-6">
            {isAnonymous() && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-md">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <span className="text-yellow-400 text-lg">⚠️</span>
                    <div>
                      <p className="text-xs font-semibold text-yellow-900">Guest Account</p>
                      <p className="text-xs text-yellow-700 mt-0.5">
                        Changes won't be saved permanently. Create an account to keep your data.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-xs font-medium ml-3"
                  >
                    Upgrade
                  </button>
                </div>
              </div>
            )}
            
            {user && !user.isAnonymous && !isEmailVerified() && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-md">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-400 text-lg">📧</span>
                    <div>
                      <p className="text-xs font-semibold text-blue-900">Email Not Verified</p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        {checkingVerification ? 'Checking...' : 'Verify your email to unlock all features'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleResendVerification}
                    disabled={loading}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium ml-3 disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Resend'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unsaved Places Banner */}
        {unsavedPlaces.length > 0 && (
          <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-3 rounded-r-md">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2 flex-1">
                <span className="text-orange-400 text-lg">💾</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-orange-900">Unsaved Places</p>
                  <p className="text-xs text-orange-700 mt-0.5">
                    {unsavedPlaces.length} place{unsavedPlaces.length !== 1 ? 's' : ''} from your last search
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowUnsavedPlaces(!showUnsavedPlaces)}
                className="text-orange-700 hover:text-orange-900 font-medium text-xs ml-3"
              >
                {showUnsavedPlaces ? '▲' : '▼'}
              </button>
            </div>
            
            {showUnsavedPlaces && (
              <div className="mt-3 space-y-2">
                <div className="bg-white border border-orange-200 rounded max-h-60 overflow-y-auto">
                  {unsavedPlaces.map((place, index) => (
                    <div key={index} className="p-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                      <p className="text-xs font-medium text-gray-900">{place.display_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{place.formatted_address}</p>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveUnsavedPlaces}
                    disabled={isSavingPlaces}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-xs font-medium disabled:opacity-50"
                  >
                    {isSavingPlaces ? 'Saving...' : `Save All (${unsavedPlaces.length})`}
                  </button>
                  <button
                    onClick={handleDiscardUnsavedPlaces}
                    disabled={isSavingPlaces}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-xs font-medium disabled:opacity-50"
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xl font-bold">
                      {(backendUser?.display_name || user?.displayName || user?.email || 'U')[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {backendUser?.display_name || user?.displayName || 'User'}
                    </h2>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <span className={`inline-flex mt-2 px-2 py-1 text-xs font-medium rounded border ${getUserTypeDisplay().color}`}>
                      {getUserTypeDisplay().label}
                    </span>
                  </div>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
                      <input
                        type="text"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Display Name</label>
                      <p className="text-sm text-gray-900">{backendUser?.display_name || user?.displayName || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                      <p className="text-sm text-gray-900">{user?.email || '—'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Stats */}
          <div className="space-y-6">
            {/* Usage Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">API Usage</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Remaining</span>
                    <span className="text-xs font-semibold text-gray-900">{getRemainingRequests()} / {getRequestLimit()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        usagePercentage > 90 ? 'bg-red-500' : usagePercentage > 70 ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.max(0, 100 - usagePercentage)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{Math.round(usagePercentage)}%</div>
                    <div className="text-xs text-gray-500 mt-0.5">used</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-600">Account Type</span>
                  <span className="text-xs font-medium text-gray-900">{getUserTypeDisplay().label}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-600">Total Limit</span>
                  <span className="text-xs font-medium text-gray-900">{getRequestLimit()}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-gray-600">Remaining</span>
                  <span className="text-xs font-medium text-gray-900">{getRemainingRequests()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;